import { useState } from 'react'

import { Box, Icon, Table, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { EmployeeAvatar } from 'modules/employees/components/EmployeeAvatar'
import ShowStatus from 'modules/payroll_bundle/components/common/components/ShowStatus'
import { getConceptByName, useConcepts } from 'modules/payroll_bundle/hooks/useConcepts'
import { useResultConceptItemsTable } from 'modules/payroll_bundle/hooks/useResultConceptItemsTable'
import { useResultValidations } from 'modules/payroll_bundle/hooks/useResultValidations'
import { usePayrollResults } from 'modules/payroll_bundle/lib/PayrollResultContext'
import {
  DEFAULT_RESULT_CONCEPT,
  DEFAULT_SORT_ATTRIBUTE,
  DEFAULT_SORT_DIRECTION,
  INITIAL_PAGE,
  NOT_PAID,
  PAGINATION_PER_PAGE,
  ResultConcepts,
  UPDATE_DATA,
  resultConceptsLabels,
} from 'modules/payroll_bundle/lib/constants'
import { ResultValidationStatus } from 'modules/payroll_bundle/lib/constants'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import {
  ResultRowData,
  getResultRowDataPaymentStatus,
} from 'modules/payroll_bundle/lib/resultsTableData'
import payments from 'modules/payroll_bundle/store/collections/payments'

import Cell from './Cell'
import EmployeeSidePanel from './EmployeeSidepanel'
import ResultConceptItemsTableOptions from './Options'
import Pagination from './Pagination'

interface Props {
  currency: string
}

const TABLE_COL_SPAN = 6
const FULL_NAME_COL_WIDTH = 250
const COL_WIDTH = 150

const ResultsTable = ({ currency }: Props) => {
  const paymentTypeLabels = resultConceptsLabels()
  const { selectedEmployeeId, sortedResultColumns, resultDispatch } = usePayrollResults()
  const [selectedResultRowData, setSelectedResultRowData] = useState<ResultRowData | null>(null)
  const { currentPeriod, prevPeriod } = usePolicyPeriods()
  const { concepts } = useConcepts()
  const currentPolicyPeriodId = currentPeriod?.id
  const previousPolicyPeriodId = prevPeriod?.id
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<{ attribute: string; direction: 'asc' | 'desc' }>({
    attribute: DEFAULT_SORT_ATTRIBUTE,
    direction: DEFAULT_SORT_DIRECTION,
  })

  function handleSort(attribute: string, direction: 'asc' | 'desc') {
    setSort({ attribute, direction })
    setCurrentPage(INITIAL_PAGE)
  }

  const { fetching, resultsTableData, paginationControls, mappedResultConcepts } =
    useResultConceptItemsTable(
      currentPolicyPeriodId,
      previousPolicyPeriodId,
      search,
      sort.attribute,
      sort.direction
    )

  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE)
  useEffect(() => {
    paginationControls.onLoadMore(currentPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  const totalPages = Math.ceil(paginationControls.totalCount / PAGINATION_PER_PAGE)

  const currentPeriodResultsTableData = resultsTableData.filter((resultData) => {
    const currentPolicyPeriodConcepts = Object.keys(resultData.conceptItems).find((key) =>
      key.endsWith(`policyPeriodId:${currentPolicyPeriodId}`)
    )
    return currentPolicyPeriodConcepts !== undefined
  })

  const netSalaryConcept = getConceptByName(concepts, DEFAULT_RESULT_CONCEPT.net_pay)
  const isEmptyList = resultsTableData.length === 0

  const resultRowData = useMemo(
    () => resultsTableData.find((rowData) => rowData.employee.id === selectedEmployeeId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedEmployeeId, resultsTableData.length]
  )

  useEffect(() => {
    if (selectedEmployeeId && resultsTableData) {
      setSelectedResultRowData(resultRowData ? resultRowData : null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployeeId, resultsTableData.length])

  const { data: resultValidations } = useResultValidations(currentPolicyPeriodId, [
    ...new Set(currentPeriodResultsTableData.map((row) => row.employee.id)),
  ])
  const toBeReviewedValidations = resultValidations.filter((item) => {
    return (
      item.status === ResultValidationStatus.warning ||
      item.status === ResultValidationStatus.invalid
    )
  })

  const toBeReviewedValidationsEmployeeIds = new Set(
    toBeReviewedValidations.map((el) => el.employee?.id)
  )

  const employeeResultValidationStatus = (employee_id: string) => {
    const validationsStatuses = toBeReviewedValidations
      .filter((item) => employee_id === item.employee?.id)
      .map((item) => item.status)

    if (validationsStatuses.includes(ResultValidationStatus.invalid)) {
      return ResultValidationStatus.invalid
    }
    return ResultValidationStatus.warning
  }

  const validationsForEmployee = (employeeId: string | undefined) => {
    return resultValidations.filter((validation) => validation.employee?.id === employeeId)
  }

  function handleSidepanelOpen(rowData: ResultRowData) {
    setSelectedResultRowData(rowData)
  }

  function handleSidepanelClose() {
    setSelectedResultRowData(null)
    if (selectedEmployeeId) {
      resultDispatch({
        type: UPDATE_DATA,
        payload: {
          selectedEmployeeId: null,
        },
      })
    }
  }

  return (
    <>
      <Table
        fullWidth
        options={() => (
          <ResultConceptItemsTableOptions
            onTableSearch={setSearch}
            mappedResultConcepts={mappedResultConcepts}
          />
        )}
        optionsBottom={() => (
          <Pagination
            itemsPerPage={PAGINATION_PER_PAGE}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={paginationControls.totalCount}
            goToPage={(newPage) => {
              setCurrentPage(newPage)
            }}
          />
        )}
      >
        {(fetching || paginationControls.loadingMore) && (
          <Table.Body>
            <Table.Loading colSpan={TABLE_COL_SPAN} />
          </Table.Body>
        )}
        {isEmptyList && !fetching && !paginationControls.loadingMore && (
          <Table.Body>
            <Table.Blank
              colSpan={TABLE_COL_SPAN}
              text={i18n.t('payroll_bundle.payroll_results.results.no_result')}
            />
          </Table.Body>
        )}
        {!isEmptyList && !fetching && !paginationControls.loadingMore && (
          <>
            <Table.Head>
              <Table.Row>
                <Table.SortableHeadCell
                  width={FULL_NAME_COL_WIDTH}
                  sticky={{ position: 'left' }}
                  attribute="name"
                  sortAttribute={sort.attribute}
                  sortDirection={sort.direction}
                  onSort={handleSort}
                >
                  {i18n.t('payroll_bundle.results_payments.results.list.header.employee')}
                </Table.SortableHeadCell>
                <Table.HeadCell width={COL_WIDTH}>
                  {i18n.t('payroll_bundle.results_payments.results.list.header.status')}
                </Table.HeadCell>
                {sortedResultColumns.map(({ label, id }) => (
                  <Table.HeadCell key={id} width={COL_WIDTH} ellipsis>
                    {label}
                  </Table.HeadCell>
                ))}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {currentPeriodResultsTableData.map((rowData) => (
                <Table.Row
                  key={rowData.id}
                  link
                  background={selectedResultRowData?.id === rowData.id ? 'grey300' : undefined}
                  onClick={() => handleSidepanelOpen(rowData)}
                >
                  <Table.Cell
                    sticky={{ position: 'left', delimiter: true, offset: 0 }}
                    width={FULL_NAME_COL_WIDTH}
                  >
                    <Box flexDirection="row" gap="s12" alignItems="center">
                      <EmployeeAvatar employee={rowData.employee} variant="large" linkProfile />
                      <Box flexDirection="column" gap="s4">
                        <Text.Normal size="200" color="grey1000">
                          {rowData.employee.access.fullName}
                        </Text.Normal>
                        <Box flexDirection="row" gap="s4">
                          {rowData.paymentType === ResultConcepts.regular_payroll &&
                            toBeReviewedValidationsEmployeeIds.has(rowData.employee.id) && (
                              <Icon.Small
                                encircled={
                                  employeeResultValidationStatus(rowData.employee.id) ===
                                  ResultValidationStatus.warning
                                    ? 'champagne400'
                                    : 'radicalRed200'
                                }
                                icon="circle"
                                color={
                                  employeeResultValidationStatus(rowData.employee.id) ===
                                  ResultValidationStatus.warning
                                    ? 'champagne1000'
                                    : 'radicalRed600'
                                }
                              />
                            )}
                          <Text.Normal size="100" color="grey800">
                            {paymentTypeLabels[rowData.paymentType]}
                          </Text.Normal>
                        </Box>
                      </Box>
                    </Box>
                  </Table.Cell>
                  <Table.Cell>
                    <Box width="fit-content">
                      <ShowStatus
                        status={
                          netSalaryConcept === null
                            ? NOT_PAID
                            : getResultRowDataPaymentStatus(
                                rowData,
                                payments,
                                currentPolicyPeriodId,
                                netSalaryConcept.id,
                                rowData.paymentType
                              )
                        }
                      />
                    </Box>
                  </Table.Cell>
                  {sortedResultColumns.map(({ id }) => (
                    <Cell
                      key={id}
                      conceptId={id}
                      resultRowData={rowData}
                      currency={currency}
                      width={COL_WIDTH}
                      currentPolicyPeriodId={currentPolicyPeriodId}
                      previousPolicyPeriodId={previousPolicyPeriodId}
                    />
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </>
        )}
      </Table>
      <EmployeeSidePanel
        selectedResultsRowData={selectedResultRowData}
        currency={currency}
        resultsTableData={resultsTableData}
        payments={payments}
        policyPeriodIds={{ current: currentPolicyPeriodId, previous: previousPolicyPeriodId }}
        onSidePanelClose={handleSidepanelClose}
        validations={validationsForEmployee(selectedResultRowData?.employee.id)}
      />
    </>
  )
}

export default observer(ResultsTable)
